import type {ChatMessage, ChatMessagePart, RequestConfig} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import type {EntityDraftApproveOption, EntityDraftGroupComponentPart, EntityDraftMessageAction} from '@Components/ai-chat-window/components/entity-draft-group/entity-draft-group.types';
import {ANSWERS_DRAFT_ID_PROP, DISCARDED_DRAFT_IDS_PROP, StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import {formatFriendlyMoment} from '@Utils/friendly-moment.util';
import {Type} from '@Components/button/button';
import type {DraftApproval, DraftApprovalButtons, DraftApproveButtonProps, DraftApproveChoice, DraftSecondaryChoice} from './draft-approve-button.types';
import {DRAFT_BUTTON_MOMENT_SEPARATOR, DraftButtonKind} from './draft-approve-button.types';

const DRAFT_COMPONENT_TYPES: StreamComponentType[] = [StreamComponentType.ENTITY_DRAFT, StreamComponentType.ENTITY_DRAFT_GROUP];

export const getDraftApproval = (messages: Record<string, ChatMessage>, draftMessageId: string): DraftApproval => {
  const draftGroups = awaitingDrafts(Object.values(messages)).filter((draft) => draftKindOf(draft) === StreamComponentType.ENTITY_DRAFT_GROUP);
  if (draftGroups.some((draft) => draft.messageId === draftMessageId && isApprovedAlone(draft))) {
    return {hasApproveButton: true, approvedTogetherCount: 1, approvesDifferentActions: false};
  }

  const approvableDrafts = draftGroups.filter((draft) => !isApprovedAlone(draft));
  const approveLabels = new Set(approvableDrafts.map(approveLabelOf).filter((approveLabel) => approveLabel !== ''));

  return {
    hasApproveButton: approvableDrafts.at(-1)?.messageId === draftMessageId,
    approvedTogetherCount: approvableDrafts.length,
    approvesDifferentActions: approveLabels.size > 1,
  };
};

/**
 * The draft's buttons: its approve button, which carries the primary option when the card offers options, and one more
 * beside it, another option or a message to send. A button approving several drafts at once offers nothing more, since
 * each card's options are its own.
 */
export const getDraftApprovalButtons = ({label, approveOptions = [], messageAction}: DraftApproveButtonProps, draftApproval: DraftApproval): DraftApprovalButtons => {
  if (draftApproval.approvedTogetherCount > 1) {
    return {primary: plainApproveChoiceOf(draftApproval.approvesDifferentActions ? window.i18next.t('pmwjs_ai_chat_draft_approve_all') : label), secondary: null};
  }

  const primaryOption = approveOptions.find((option) => option.primary);
  const secondaryOption = approveOptions.find((option) => !option.primary);
  const primary = primaryOption === undefined ? plainApproveChoiceOf(label) : optionChoiceOf(primaryOption);

  return {primary, secondary: secondaryChoiceOf(secondaryOption === undefined ? null : optionChoiceOf(secondaryOption), messageAction)};
};

/** The approve button is filled when a second button sits beside it; alone, it keeps the outline every one-button draft card has. */
export const approveButtonTypeOf = (buttons: DraftApprovalButtons): Type => {
  return buttons.secondary === null ? Type.SECONDARY : Type.PRIMARY;
};

/** What a button shows: its label, and after it the moment an approve button carries, worded on today's date. */
export const shownLabelOf = (choice: DraftSecondaryChoice): string => {
  if (choice.kind === DraftButtonKind.MESSAGE || choice.moment === undefined) {
    return choice.label;
  }

  return `${choice.label}${DRAFT_BUTTON_MOMENT_SEPARATOR}${formatFriendlyMoment(choice.moment)}`;
};

/** What pressing an approve button sends: the draft it approves, and the option it carries when it carries one. */
export const approvalRequestOf = (draftId: string, choice: DraftApproveChoice): RequestConfig => {
  return choice.optionId === null ? {approvedDraftId: draftId} : {approvedDraftId: draftId, approvedOptionId: choice.optionId};
};

const plainApproveChoiceOf = (label: string): DraftApproveChoice => {
  return {kind: DraftButtonKind.APPROVE, label, optionId: null};
};

const optionChoiceOf = (option: EntityDraftApproveOption): DraftApproveChoice => {
  return {kind: DraftButtonKind.APPROVE, label: option.label, optionId: option.id, moment: option.moment};
};

const secondaryChoiceOf = (secondaryApproval: DraftApproveChoice | null, messageAction: EntityDraftMessageAction | undefined): DraftSecondaryChoice | null => {
  if (secondaryApproval !== null) {
    return secondaryApproval;
  }

  return messageAction === undefined ? null : {kind: DraftButtonKind.MESSAGE, label: messageAction.label, message: messageAction.message};
};

/**
 * A draft waits while it belongs to the latest turn that showed a draft of its own kind and nothing answers it,
 * so a later draft of another kind (another domain's) never ends it.
 */
const awaitingDrafts = (orderedMessages: ChatMessage[]): ChatMessage[] => {
  const answeredDraftIds = orderedMessages.flatMap(draftIdsAnsweredBy);
  const latestTurnStartByKind = latestDraftTurnStartByKind(orderedMessages);

  return orderedMessages.filter((message, position) => {
    const kind = draftKindOf(message);
    if (kind === null || message.messageId === undefined) {
      return false;
    }

    return position >= (latestTurnStartByKind.get(kind) ?? 0) && !answeredDraftIds.includes(message.messageId);
  });
};

const latestDraftTurnStartByKind = (orderedMessages: ChatMessage[]): Map<StreamComponentType, number> => {
  const latestTurnStartByKind = new Map<StreamComponentType, number>();
  let turnStart = 0;

  orderedMessages.forEach((message, position) => {
    if (message.role === MessageRole.USER) {
      turnStart = position + 1;
      return;
    }

    const kind = draftKindOf(message);
    if (kind !== null) {
      latestTurnStartByKind.set(kind, turnStart);
    }
  });

  return latestTurnStartByKind;
};

const draftKindOf = (message: ChatMessage): StreamComponentType | null => {
  for (const part of message.parts) {
    if (part.type === ChatMessagePartType.COMPONENT && DRAFT_COMPONENT_TYPES.includes(part.componentType)) {
      return part.componentType;
    }
  }

  return null;
};

const approveLabelOf = (draft: ChatMessage): string => {
  return draft.parts.find(isDraftGroupPart)?.props.approveLabel ?? '';
};

const isApprovedAlone = (draft: ChatMessage): boolean => {
  return draft.parts.find(isDraftGroupPart)?.props.approvedAlone === true;
};

const isDraftGroupPart = (part: ChatMessagePart): part is EntityDraftGroupComponentPart => {
  return part.type === ChatMessagePartType.COMPONENT && part.componentType === StreamComponentType.ENTITY_DRAFT_GROUP;
};

const draftIdsAnsweredBy = (message: ChatMessage): string[] => {
  return message.parts.flatMap((part) => {
    if (part.type !== ChatMessagePartType.COMPONENT) {
      return [];
    }

    const answeredDraftId = part.props[ANSWERS_DRAFT_ID_PROP];
    const answeredDraftIds = Array.isArray(answeredDraftId) ? answeredDraftId : [answeredDraftId];
    const discardedDraftIds = part.props[DISCARDED_DRAFT_IDS_PROP] ?? [];

    return [...answeredDraftIds, ...discardedDraftIds].filter((draftId): draftId is string => typeof draftId === 'string');
  });
};
